import React, { useRef, useState } from 'react'
import styled from 'styled-components'
import { useInboxStore } from '../../../../../stores/inbox/useInboxStore'
import { useInboxHandlers } from '../../../../../services/actions/inbox/useInboxHandlers'
import { useConnectionsRequest } from '../../../../../services/apis/connections/useConnectionsRequest'

const SEARCH_DEBOUNCE_MS = 300

const ContactInput: React.FC = () => {
    const [searchTerm, setSearchTerm] = useState('')
    const [results, setResults] = useState<any[]>([])
    const [loading, setLoading] = useState(false)
    const searchSeqRef = useRef(0)
    const debounceTimerRef = useRef<number | null>(null)
    const { store: { selectedParticipants } } = useInboxStore()
    const { setSelectedParticipants } = useInboxHandlers()
    const { getConnections } = useConnectionsRequest()

    const handleSearch = async (value: string) => {
        setSearchTerm(value)
        // Bump on every keystroke so any in-flight request becomes stale immediately.
        const inputSeq = ++searchSeqRef.current
        if (debounceTimerRef.current) {
            window.clearTimeout(debounceTimerRef.current)
            debounceTimerRef.current = null
        }
        if (!value.trim()) {
            setResults([])
            setLoading(false)
            return
        }

        debounceTimerRef.current = window.setTimeout(async () => {
            setLoading(true)
            try {
                const data: any = await getConnections(
                    { searchByName: value, pageSize: 10, isInboxSearch: true },
                    'inboxSearchConnections'
                )
                // Only the latest request may update results (prevents stale responses overriding newer ones)
                if (inputSeq !== searchSeqRef.current) return
                setResults(data?.connections ?? [])
            } catch {
                if (inputSeq !== searchSeqRef.current) return
                setResults([])
            } finally {
                if (inputSeq === searchSeqRef.current) setLoading(false)
            }
        }, SEARCH_DEBOUNCE_MS)
    }

    const addParticipant = (connection: any) => {
        if (!selectedParticipants.some((p: any) => p._id === connection._id)) {
            setSelectedParticipants([...selectedParticipants, connection])
        }
        setSearchTerm('')
        setResults([])
    }

    const removeParticipant = (id: string) => {
        setSelectedParticipants(selectedParticipants.filter((p: any) => p._id !== id))
    }

    return (
        <Container>
            <Label>To:</Label>
            <ChipsWrapper>
                {selectedParticipants.map((p: any) => (
                    <Chip key={p._id}>
                        {[p.firstName, p.lastName].filter(Boolean).join(' ') || 'Unknown'}
                        <RemoveChip onClick={() => removeParticipant(p._id)}>×</RemoveChip>
                    </Chip>
                ))}
                <SearchInput
                    value={searchTerm}
                    onChange={e => handleSearch(e.target.value)}
                    placeholder={selectedParticipants.length ? '' : 'Search connections...'}
                />
            </ChipsWrapper>
            {results.length > 0 && (
                <Dropdown>
                    {results.map((conn: any) => (
                        <DropdownItem key={conn._id} onClick={() => addParticipant(conn)}>
                            <Photo src={conn.profilePicture} alt="" />
                            <span>{[conn.firstName, conn.lastName].filter(Boolean).join(' ') || 'Unknown'}</span>
                        </DropdownItem>
                    ))}
                </Dropdown>
            )}
        </Container>
    )
}

const Container = styled.div`
    position: relative;
    width: 100%;
    min-height: 52px;
    border-radius: 4px;
    padding: 12px 16px;
    background-color: ${props => props.theme.secondaryColor || '#fff'};
    margin-bottom: 2px;
    display: flex;
    align-items: flex-start;
    gap: 8px;
`
const Label = styled.span`
    font-size: 14px;
    font-weight: bold;
    color: #8c96ad;
    line-height: 28px;
    flex-shrink: 0;
`
const ChipsWrapper = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    flex: 1;
    align-items: center;
`
const Chip = styled.span`
    display: inline-flex;
    align-items: center;
    gap: 4px;
    background: #eef3ff;
    color: #5964ff;
    border-radius: 14px;
    padding: 2px 10px 2px 10px;
    font-size: 13px;
    font-weight: 600;
`
const RemoveChip = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    color: #5964ff;
    font-size: 16px;
    line-height: 1;
    padding: 0;
    &:hover { color: #ff6760; }
`
const SearchInput = styled.input`
    border: none;
    outline: none;
    font-size: 14px;
    color: ${props => props.theme.primaryColor};
    background: transparent;
    min-width: 120px;
    flex: 1;
    line-height: 28px;
`
const Dropdown = styled.div`
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    background: white;
    border: 1px solid #dae0e8;
    border-radius: 8px;
    box-shadow: 0 4px 16px rgba(0,0,0,0.1);
    z-index: 100;
    max-height: 240px;
    overflow-y: auto;
`
const DropdownItem = styled.div`
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 8px 12px;
    cursor: pointer;
    font-size: 14px;
    &:hover { background: #f6f8fb; }
`
const Photo = styled.img`
    width: 32px;
    height: 32px;
    border-radius: 50%;
    object-fit: cover;
    background: #e5e5e5;
`

export default ContactInput
